import { useTranslation } from "next-i18next";

import { ProductBySlugNonNullable } from "@/common/contracts/pdp.contracts";
import { UsDeliveryFacts } from "@/features/us-delivery/components/UsDeliveryFacts";
import { UsDeliveryQuote } from "@/features/us-delivery/components/UsDeliveryQuote";
import { UsPostalDelivery } from "@/features/us-delivery/components/UsPostalDelivery";
import { UsDeliveryKind } from "@/features/us-delivery/us-delivery.utils";
import { Flavor } from "@/ui/typography/Flavor";

type ProductDetailsUsDeliveryProps = {
  product: ProductBySlugNonNullable;
  kind: Exclude<UsDeliveryKind, "none">;
};

/**
 * WHO-6509. The product page's delivery block on `/en-us` for a listing that ships to the US:
 * the quote flow for a courier listing, Sendcloud's US rate (or why there is none) for a postal
 * one, and the delivery time and duties note. Guests see it too; only asking for a quote needs an
 * account. Never today's European options: they are priced for a European location.
 */
export const ProductDetailsUsDelivery = ({
  product,
  kind,
}: ProductDetailsUsDeliveryProps) => {
  const { t } = useTranslation();

  return (
    <section
      className="flex flex-col gap-4 border border-grey-300 p-4"
      data-testid="pdp.us-delivery"
      data-us-delivery-kind={kind}
    >
      <Flavor size="medium" className="font-bold">
        {t("pdp:pdp-us-delivery-title", {
          defaultValue: "Shipping to the United States",
        })}
      </Flavor>
      {kind === "quote" ? (
        <>
          <UsDeliveryQuote product={product} />
          <UsDeliveryFacts />
        </>
      ) : (
        /* The postal block decides itself whether the facts still apply. */
        <UsPostalDelivery productId={product.id} />
      )}
    </section>
  );
};
